import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  CircularProgress,
  SelectChangeEvent,
  TextField,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  PlayArrow,
  DatasetOutlined,
  SettingsOutlined,
  PreviewOutlined,
  CheckCircleOutline,
  Schedule as ScheduleIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import apiService from '../services/api';
import { useDateRange } from '../contexts/DateRangeContext';
import SimpleProgressTracker from '../components/analysis/SimpleProgressTracker';

import {
  AnalysisRequest,
  AnalysisType,
  AnalysisParameters,
  AnalysisJob,
  JobStatus
} from '../types';
import { WatchlistDevice } from '../types/scheduled';

const AnalysisTypeSelector = React.lazy(() => import('../components/analysis/AnalysisTypeSelector'));
const AnalysisParametersForm = React.lazy(() => import('../components/analysis/AnalysisParametersForm'));

interface AnalysisConfig {
  device_ids: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  analysisType: AnalysisType;
  parameters: AnalysisParameters;
  notification_email?: string; // Email for completion notification
}

const steps = [
  'Configure Data Selection',
  'Set Analysis Parameters',
  'Review & Execute',
  'View Results'
];

// Default parameters match Full Analysis (all toggles enabled)
const defaultParameters: AnalysisParameters = {
  enable_dwell_detection: true,
  enable_dwell_based_routes: true,
  enable_non_dwell_routes: true,
  enable_temporal_analysis: true,
  enable_connectivity_analysis: true,
  enable_route_consistency: true,
  enable_place_lookup: true,
  enable_ai_insights: true,
  dwell_radius: 50.0,
  stay_point_time_threshold: 300,
  stay_point_distance_threshold: 50.0,
  min_route_length: 5,
  min_segment_distance: 100.0,
  route_similarity_threshold: 50.0,
  max_dwell_locations: 100,
  max_routes: 100,
};

const AnalysisPage: React.FC = () => {
  const theme = useTheme();
  const { startDate, endDate } = useDateRange();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [currentJob, setCurrentJob] = useState<AnalysisJob | null>(null);
  const [initialSyncDone, setInitialSyncDone] = useState(false);
  
  // Watchlist devices state
  const [watchlistDevices, setWatchlistDevices] = useState<WatchlistDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  
  // Initialize with global date range, fallback to last 7 days if not set
  const getInitialDateRange = () => {
    if (startDate && endDate) {
      return { start: startDate, end: endDate };
    }
    // Default to last 7 days
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    return { start, end };
  };
  
  const [config, setConfig] = useState<AnalysisConfig>({
    device_ids: [],
    dateRange: getInitialDateRange(),
    analysisType: AnalysisType.FULL,
    parameters: defaultParameters,
    notification_email: ''
  });
  
  // Load watchlist devices on mount
  useEffect(() => {
    setLoadingDevices(true);
    apiService.getWatchlist()
      .then((devices) => {
        setWatchlistDevices(devices);
      })
      .catch((err) => {
        console.error('Failed to load watchlist devices:', err);
      })
      .finally(() => {
        setLoadingDevices(false);
      });
  }, []);
  
  // Start analysis mutation
  const startAnalysisMutation = useMutation({
    mutationFn: (request: AnalysisRequest) => apiService.startAnalysis(request),
    onSuccess: (job) => {
      setCurrentJob(job);
      setActiveStep(3); // Move to results step
    },
    onError: (_: any) => {
    }
  });

  const handleConfigChange = useCallback((updates: Partial<AnalysisConfig>) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      
      // 🔧 FIX: If analysis type changed, update parameters to match
      if (updates.analysisType && updates.analysisType !== prev.analysisType) {
        // Import the parameter update logic
        const updateParametersForType = (type: AnalysisType) => {
          const defaultParams = {
            enable_dwell_detection: false,
            enable_dwell_based_routes: false,
            enable_non_dwell_routes: false,
            enable_temporal_analysis: false,
            enable_connectivity_analysis: false,
            enable_route_consistency: false,
            enable_place_lookup: false,
            enable_ai_insights: false,
            dwell_radius: 50.0,
            stay_point_time_threshold: 300,
            stay_point_distance_threshold: 50.0,
            min_route_length: 5,
            min_segment_distance: 100.0,
            route_similarity_threshold: 50.0,
            max_dwell_locations: 100,
            max_routes: 100,
          };

          switch (type) {
            case AnalysisType.QUICK:
              return {
                ...defaultParams,
                enable_dwell_detection: true,
                enable_temporal_analysis: true,
                enable_place_lookup: false,
                enable_ai_insights: false,
              };
            case AnalysisType.FULL:
              return {
                ...defaultParams,
                enable_dwell_detection: true,
                enable_dwell_based_routes: true,
                enable_non_dwell_routes: true,
                enable_temporal_analysis: true,
                enable_connectivity_analysis: true,
                enable_route_consistency: true,
                enable_place_lookup: true,
                enable_ai_insights: true,  // AI enabled for Full Analysis
              };
            case AnalysisType.ROUTE_ONLY:
              return {
                ...defaultParams,
                enable_dwell_detection: true,
                enable_dwell_based_routes: true,
                enable_non_dwell_routes: true,
                enable_route_consistency: true,
                enable_place_lookup: false,
                enable_ai_insights: false,
              };
            case AnalysisType.TEMPORAL_ONLY:
              return {
                ...defaultParams,
                enable_dwell_detection: true,
                enable_temporal_analysis: true,
                enable_place_lookup: false,
                enable_ai_insights: false,
              };
            case AnalysisType.MULTI_DEVICE:
              return {
                ...defaultParams,
                enable_dwell_detection: true,
                enable_dwell_based_routes: true,
                enable_non_dwell_routes: true,
                enable_temporal_analysis: true,
                enable_connectivity_analysis: true,
                enable_route_consistency: true,
                enable_place_lookup: true,
                enable_ai_insights: true,  // AI enabled for Multi-Device Analysis
              };
            default:
              return defaultParams;
          }
        };
        
        const updatedParams = updateParametersForType(updates.analysisType);
        newConfig.parameters = updatedParams;
        
      }
      
      return newConfig;
    });
  }, []);

  // 🔧 FIX: Sync initial parameters with initial analysis type on mount (after handleConfigChange is defined)
  useEffect(() => {
    if (!initialSyncDone) {
      // Force parameter sync on mount to match initial Full Analysis selection
      handleConfigChange({ 
        analysisType: config.analysisType  // This will trigger parameter update logic
      });
      
      setInitialSyncDone(true);
    }
  }, [initialSyncDone, config.analysisType, handleConfigChange]);

  const handleStartAnalysis = useCallback(() => {
    // Check if we have devices selected
    if (config.device_ids.length === 0) {
      alert('Please select at least one device');
      return;
    }

    const request: AnalysisRequest = {
      name: `${config.analysisType} Analysis - ${new Date().toLocaleDateString()}`,
      analysis_type: config.analysisType,
      device_type: null, // Single device type, not needed
      device_ids: config.device_ids,
      date_range_start: config.dateRange.start?.toISOString(),
      date_range_end: config.dateRange.end?.toISOString(),
      parameters: config.parameters,
      notification_email: config.notification_email || undefined
    };

    startAnalysisMutation.mutate(request);
  }, [config, startAnalysisMutation]);

  const handleNext = () => {
    const newStep = Math.min(activeStep + 1, steps.length - 1);
    // 🔧 FIX: When moving TO parameters step, ensure parameters match current analysis type
    if (newStep === 1) {  // Moving to "Set Analysis Parameters" step
      // Force parameter update to match current analysis type
      handleConfigChange({ 
        analysisType: config.analysisType  // This will trigger the parameter update logic
      });
    }
    
    setActiveStep(newStep);
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  // Helper to get device display name
  const getDeviceDisplayName = (registrationId: string): string => {
    const device = watchlistDevices.find(d => d.registration_id === registrationId);
    if (device?.name) return device.name;
    return registrationId.length > 12 ? `${registrationId.substring(0, 12)}...` : registrationId;
  };

  // Allow analysis via postgres even when there are no datasets

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: 'calc(100vh - 64px)', // Full height minus navbar
      bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
      overflow: 'hidden'
    }}>
      {/* Compact Header Bar - similar to Pathfinder */}
      <Paper 
        elevation={0}
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <RouteIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Route Analysis
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Advanced GPS tracking analysis with AI-powered insights
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ScheduleIcon />}
          onClick={() => navigate('/scheduled-analyses')}
          sx={{ fontWeight: 500 }}
        >
          View Schedules
        </Button>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 3,
      }}>
        {/* Configuration Stepper */}
        <Card sx={{ 
          maxWidth: '1100px', 
          mx: 'auto',
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
          boxShadow: theme.palette.mode === 'dark' ? 'none' : 2,
          border: theme.palette.mode === 'dark' ? 1 : 0,
          borderColor: 'divider',
          borderRadius: 2
        }}>
          <CardContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
                  {/* Step 1: Data Selection */}
                  <Step>
                    <StepLabel 
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: activeStep >= 0 ? 'primary.main' : (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'),
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}>
                          <DatasetOutlined fontSize="small" />
                        </Box>
                      )}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Configure Data Selection</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Choose devices, date range, and analysis type
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {/* Two-column layout for Device/Date on left and Info boxes on right */}
                      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                        {/* Left Column: Device Selection and Date Range */}
                        <Box sx={{ flex: '0 0 500px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {/* Device Selection */}
                          <FormControl fullWidth>
                            <InputLabel>Saved Devices (Watchlist)</InputLabel>
                            <Select
                              multiple
                              value={config.device_ids}
                              onChange={(e: SelectChangeEvent<string[]>) => {
                                const value = e.target.value;
                                handleConfigChange({ 
                                  device_ids: typeof value === 'string' ? value.split(',') : value
                                });
                              }}
                              input={<OutlinedInput label="Saved Devices (Watchlist)" />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((value) => (
                                    <Chip 
                                      key={value} 
                                      label={getDeviceDisplayName(value)} 
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              )}
                              disabled={loadingDevices}
                            >
                              {loadingDevices ? (
                                <MenuItem disabled>
                                  <CircularProgress size={20} sx={{ mr: 1 }} />
                                  Loading devices...
                                </MenuItem>
                              ) : watchlistDevices.length === 0 ? (
                                <MenuItem disabled>
                                  <Typography color="text.secondary">
                                    No saved devices. Add devices to your watchlist first.
                                  </Typography>
                                </MenuItem>
                              ) : (
                                watchlistDevices.map((device) => (
                                  <MenuItem key={device.registration_id} value={device.registration_id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <Typography variant="body2" fontWeight={500}>
                                        {device.name || device.registration_id}
                                      </Typography>
                                      {device.name && (
                                        <Typography variant="caption" color="text.secondary">
                                          {device.registration_id}
                                        </Typography>
                                      )}
                                    </Box>
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                          
                          {/* Date Range Picker - Without info boxes */}
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              Date Range Selection
                            </Typography>

                            {/* Quick Selection Buttons */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Quick Select:
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip
                                  label="Last 7 Days"
                                  onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(end.getDate() - 6);
                                    handleConfigChange({ dateRange: { start, end } });
                                  }}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                />
                                <Chip
                                  label="Last 14 Days"
                                  onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(end.getDate() - 13);
                                    handleConfigChange({ dateRange: { start, end } });
                                  }}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                />
                                <Chip
                                  label="Last 30 Days"
                                  onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(end.getDate() - 29);
                                    handleConfigChange({ dateRange: { start, end } });
                                  }}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                />
                                <Chip
                                  label="Last 90 Days"
                                  onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(end.getDate() - 89);
                                    handleConfigChange({ dateRange: { start, end } });
                                  }}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                />
                              </Stack>
                            </Box>

                            {/* Date Input Fields */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <TextField
                                label="Start Date"
                                type="date"
                                value={config.dateRange.start ? config.dateRange.start.toISOString().split('T')[0] : ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const date = e.target.value ? new Date(e.target.value) : null;
                                  handleConfigChange({ dateRange: { start: date, end: config.dateRange.end } });
                                }}
                                onClick={(e) => {
                                  // Make the entire field clickable to open the date picker
                                  const input = e.currentTarget.querySelector('input');
                                  if (input) {
                                    (input as any).showPicker?.();
                                  }
                                }}
                                sx={{ 
                                  minWidth: 200,
                                  cursor: 'pointer',
                                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    filter: 'invert(1)',
                                    cursor: 'pointer',
                                    opacity: 0.7,
                                    '&:hover': {
                                      opacity: 1
                                    }
                                  }
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                  max: config.dateRange.end ? config.dateRange.end.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
                                }}
                              />
                              <TextField
                                label="End Date"
                                type="date"
                                value={config.dateRange.end ? config.dateRange.end.toISOString().split('T')[0] : ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const date = e.target.value ? new Date(e.target.value) : null;
                                  handleConfigChange({ dateRange: { start: config.dateRange.start, end: date } });
                                }}
                                onClick={(e) => {
                                  // Make the entire field clickable to open the date picker
                                  const input = e.currentTarget.querySelector('input');
                                  if (input) {
                                    (input as any).showPicker?.();
                                  }
                                }}
                                sx={{ 
                                  minWidth: 200,
                                  cursor: 'pointer',
                                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    filter: 'invert(1)',
                                    cursor: 'pointer',
                                    opacity: 0.7,
                                    '&:hover': {
                                      opacity: 1
                                    }
                                  }
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                  min: config.dateRange.start ? config.dateRange.start.toISOString().split('T')[0] : undefined,
                                  max: new Date().toISOString().split('T')[0]
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Right Column: Info Box */}
                        <Box sx={{ flex: 1 }}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2, 
                              bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1
                            }}
                          >
                            {/* Selected Range Summary */}
                            {config.dateRange.start && config.dateRange.end && config.dateRange.start <= config.dateRange.end && (
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                <strong>Selected:</strong> {config.dateRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {config.dateRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ({Math.floor((config.dateRange.end.getTime() - config.dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1} days)
                              </Typography>
                            )}
                            
                            {/* Tips */}
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
                              <strong>Tips:</strong> Smaller ranges (7-14 days) = faster analysis • Larger ranges (30+ days) = more comprehensive patterns • Consider weekend vs weekday patterns
                            </Typography>
                          </Paper>
                        </Box>
                      </Box>

                      {/* Analysis Type Selector - Full Width Below */}
                      <Box>
                        <React.Suspense fallback={<LinearProgress sx={{ width: '100%', mb: 2 }} />}>
                          <AnalysisTypeSelector
                            analysisType={config.analysisType}
                            onChange={(analysisType) =>
                              handleConfigChange({ analysisType })
                            }
                          />
                        </React.Suspense>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{ mr: 1 }}
                        >
                          Continue
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 2: Analysis Parameters */}
                  <Step>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: activeStep >= 1 ? 'primary.main' : (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'),
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}>
                          <SettingsOutlined fontSize="small" />
                        </Box>
                      )}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Set Analysis Parameters</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Configure detection thresholds and analysis options
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <React.Suspense fallback={<LinearProgress sx={{ width: '100%', mb: 2 }} />}>
                        <AnalysisParametersForm
                          parameters={config.parameters}
                          onChange={(parameters) => 
                            handleConfigChange({ parameters })
                          }
                        />
                      </React.Suspense>
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{ mr: 1 }}
                        >
                          Continue
                        </Button>
                        <Button onClick={handleBack}>
                          Back
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 3: Review & Execute */}
                  <Step>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: activeStep >= 2 ? 'primary.main' : (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'),
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}>
                          <PreviewOutlined fontSize="small" />
                        </Box>
                      )}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Review & Execute</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Verify configuration and start analysis
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2, 
                              height: '100%',
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'primary.50',
                              border: 1,
                              borderColor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.100',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                              Devices
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                              {config.device_ids.length > 0 
                                ? config.device_ids.map(id => getDeviceDisplayName(id)).join(', ') 
                                : 'No devices selected'}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2, 
                              height: '100%',
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'primary.50',
                              border: 1,
                              borderColor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.100',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                              Date Range
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                              {config.dateRange.start?.toLocaleDateString()} - {config.dateRange.end?.toLocaleDateString()}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2, 
                              height: '100%',
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(2, 136, 209, 0.08)' : 'info.50',
                              border: 1,
                              borderColor: theme.palette.mode === 'dark' ? 'info.dark' : 'info.100',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                              Analysis Type
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                              {config.analysisType.replace('_', ' ')}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2, 
                              height: '100%',
                              bgcolor: config.parameters.enable_ai_insights 
                                ? (theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.08)' : 'success.50')
                                : (theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100'),
                              border: 1,
                              borderColor: config.parameters.enable_ai_insights 
                                ? (theme.palette.mode === 'dark' ? 'success.dark' : 'success.100')
                                : 'divider',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                              AI Insights
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                              {config.parameters.enable_ai_insights ? 'Enabled' : 'Disabled'}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                      
                      {/* Email notification option */}
                      <Box sx={{ mt: 3, mb: 2 }}>
                        <TextField
                          label="Notification Email (optional)"
                          type="email"
                          value={config.notification_email || ''}
                          onChange={(e) => setConfig(prev => ({ ...prev, notification_email: e.target.value }))}
                          fullWidth
                          size="small"
                          helperText="Receive an email when analysis completes"
                          sx={{ maxWidth: 400 }}
                        />
                      </Box>
                      
                      {startAnalysisMutation.isPending && (
                        <LinearProgress sx={{ mb: 2 }} />
                      )}
                      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          startIcon={<PlayArrow />}
                          onClick={handleStartAnalysis}
                          disabled={startAnalysisMutation.isPending}
                          color="success"
                          size="large"
                        >
                          {startAnalysisMutation.isPending ? 'Starting...' : 'Run Now'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<ScheduleIcon />}
                          onClick={() => {
                            // Store current config in localStorage for schedule dialog
                            localStorage.setItem('pendingScheduleConfig', JSON.stringify(config));
                            navigate('/scheduled-analyses');
                          }}
                          size="large"
                        >
                          Schedule This
                        </Button>
                        <Button onClick={handleBack} disabled={startAnalysisMutation.isPending}>
                          Back
                        </Button>
                      </Stack>
                    </StepContent>
                  </Step>

                  {/* Step 4: Results */}
                  <Step>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: activeStep >= 3 ? 'success.main' : (theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'),
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}>
                          <CheckCircleOutline fontSize="small" />
                        </Box>
                      )}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>View Results</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Analysis complete - view full report
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {currentJob && currentJob.status === JobStatus.COMPLETED && (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body1">
                              Analysis completed successfully!
                            </Typography>
                          </Alert>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              // Navigate to results page
                              window.location.href = `/results/${currentJob.id}`;
                            }}
                          >
                            View Full Results
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
          </Stepper>
        </CardContent>
      </Card>

      {/* Progress Tracker */}
      {currentJob && (
        <Card sx={{ 
          mt: 3, 
          maxWidth: '1100px', 
          mx: 'auto',
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
          boxShadow: theme.palette.mode === 'dark' ? 'none' : 2,
          border: theme.palette.mode === 'dark' ? 1 : 0,
          borderColor: 'divider',
          borderRadius: 2
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Analysis in Progress
            </Typography>
            <React.Suspense fallback={<LinearProgress sx={{ width: '100%', mb: 2 }} />}>
              <SimpleProgressTracker job={currentJob} onJobComplete={(job) => {
                setCurrentJob(job);
                // Move to results step when job completes  
                if (job.status === JobStatus.COMPLETED) {
                  setActiveStep(3);
                }
              }} />
            </React.Suspense>
          </CardContent>
        </Card>
      )}
      </Box>
    </Box>
  );
};

export default AnalysisPage;
